(async () => {
    const { Core } = await importModule('Core');
    const { Swiper } = await importModule('Swiper');

    const NAV_ARROWS = 'arrows';
    const NAV_PAG = 'pagination';
    const CN_AUTOPLAY = '@autoplay';
    const SP_AUTOPLAY = '--autoplay';
    const ATTR_AUTOPLAY = 'autoplay';
    const CN_NAV_DISABLED = '!disabled';
    const SEL_BG_VIDEO = 'background-video';
    const SEL_VIDEO_SLIDE = '[data-video-slide]';
    const ATTR_HAS_VIDEO = 'has-video';
    const ATTR_NAVIGATION = 'navigation';
    const ATTR_AUTO_HEIGHT = 'auto-height';
    
    customElements.define('slideshow-hero', class extends Core {
        elements = {
            swiper: '[data-swiper]',
            next: '[data-next]',
            prev: '[data-prev]'
        }

        render() {
            // containerInnerWidth is set synchronously during HTML parsing by _js-vars.liquid
            // (containerElInner.offsetWidth) — no forced layout, correct in PSI emulation.
            // Mobile (< 992px): rewind mode — no DOM clones, so LCP element stays at left:0
            //   throughout Swiper init, eliminating the ~1-2s simulated elementRenderDelay.
            // Desktop (≥ 992px): loop mode — seamless infinite autoplay.
            this._useLoop = (window.containerInnerWidth || window.innerWidth || 0) >= 992;
            this._setSwiperProps();
            this._initSwiper();
            this._syncAutoplay();
            this._initAutoplay();
        }
        
        _initSwiper() {
            this.swiper = new Swiper(this.$swiper, this.swiperProps);
            this.hasVideo && this.swiper.on('slideChange', this._toggleVideo.bind(this));
        }

        _initAutoplay() {
            if(!this.autoplay) return;
            // Apply the CSS timing indicator once autoplay is actually running.
            // Check immediately (in case breakpoint activated it during init)
            // and also listen for future start/stop events.
            const activate = () => {
                this.classList.add(CN_AUTOPLAY);
                this.style.setProperty(SP_AUTOPLAY, `${this.autoplay}ms`);
            };
            if(this.swiper.autoplay && this.swiper.autoplay.running) activate();
            this.swiper.on('autoplayStart', activate);
            this.swiper.on('autoplayStop', () => this.classList.remove(CN_AUTOPLAY));
        }

        _setSwiperProps() {
            // loop: true  → Swiper inserts DOM clones; seamless infinite scroll on desktop.
            // rewind: true → No DOM clones; LCP element never repositioned; used on mobile.
            this.swiperProps = this._useLoop ? { loop: true } : { rewind: true };
            this.swiperProps.autoHeight = this.autoHeight;

            if(this.hasVideo) 
                this.swiperProps.on = {
                    init: () => {
                        window.customElements.whenDefined(SEL_BG_VIDEO).then(() => {
                            this.videoSlides = Array.from(this.querySelectorAll(SEL_VIDEO_SLIDE)).map(video => {
                                video.querySelector(SEL_BG_VIDEO).initPlayer();
                                return video;
                            })
                        })
                    }
                }

            if(this.navigation == NAV_ARROWS)
                this.swiperProps.navigation = {
                    nextEl: this.$next,
                    prevEl: this.$prev,
                    disabledClass: CN_NAV_DISABLED
                }

            if(this.navigation == NAV_PAG) 
                this.swiperProps.pagination = {
                    el: '.swiper-pagination',
                    type: 'bullets',
                    clickable: true
                }
            
            if(this.autoplay) {
                this.swiperProps.autoplay = {
                    delay: this.autoplay,
                    disableOnInteraction: false
                };
            }
        }

        _syncAutoplay() {
            if(!this.autoplay || !this.swiper?.autoplay) return;

            if(this._useLoop) {
                this.swiper.autoplay.start();
            } else {
                this.swiper.autoplay.stop();
            }
        }

        _toggleVideo({ slides, activeIndex, previousIndex }) {
            const current = this.videoSlides.find((item) => item === slides[activeIndex]);
            const prev = this.videoSlides.find((item) => item === slides[previousIndex]);
            current?.querySelector(SEL_BG_VIDEO)?.play();
            prev?.querySelector(SEL_BG_VIDEO)?.pause();            
        }

        get navigation() {
            return this.getAttribute(ATTR_NAVIGATION);
        }
        get hasVideo() {
            return this.hasAttribute(ATTR_HAS_VIDEO);
        }
        get autoplay() {
            return this.getAttribute(ATTR_AUTOPLAY) && +this.getAttribute(ATTR_AUTOPLAY);
        }
        get autoHeight() {
            return this.hasAttribute(ATTR_AUTO_HEIGHT);
        }
    })
})();
